/**
 * Self-test suite: GIF / sticker (proxy + manifest)
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/GifStickerSelfTest.swift`.
 *
 * 14 cases across four groups:
 *
 *   A. Proxy URL validation:
 *      A1. valid https://cdn.kaether.fr/gifproxy/v1/<path> → accepted
 *      A2. http:// variant → rejected (non-HTTPS)
 *      A3. https://evil.com/gifproxy/v1/... → rejected (wrong host)
 *      A4. query path contains :// → rejected (external-redirect attempt)
 *      A5. response redirect chain all-proxy → accepted
 *      A6. redirect chain with one external step → rejected
 *
 *   B. GifSearchResultItem shape:
 *      B1. type has no trackingPixel / analyticsId field (type-level)
 *      B2. rewriteProviderURL rewrites giphy.com URL through proxy
 *      B3. rewriteProviderURL rewrites tenor.com URL through proxy
 *      B4. rewriteProviderURL rejects http:// input
 *
 *   C. Sticker manifest — valid round-trip:
 *      C1. encode → decode preserves all sticker fields
 *      C2. Ed25519 signature verifies (implicit in C1, explicit separate check)
 *
 *   D. Sticker manifest — rejection cases:
 *      D1. 0 stickers → emptyPack error on decode
 *      D2. 201 stickers → tooManyStickers error on encode (before it reaches decode)
 *      D3. duplicate sticker ID → duplicateStickerIds error
 *      D4. sticker URL fails validateProxyURL → invalidStickerURL error
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import {
  PROXY_BASE_URL,
  validateProxyURL,
  validateResponseURL,
} from "./GifProxyPolicy";
import {
  type GifSearchResultItem,
  rewriteProviderURL,
} from "./GifSearchResult";
import {
  encodeStickerPack,
  decodeStickerPack,
  generateAuthorKeyPair,
  STICKER_PACK_MAX_STICKERS,
  StickerManifestError,
  type StickerEntry,
} from "./StickerManifest";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

function freshSticker(id: string): StickerEntry {
  return {
    id,
    url: `${PROXY_BASE_URL}giphy/${id}.gif`,
    altText: `sticker ${id}`,
  };
}

// ---------------------------------------------------------------------------
// Group A: Proxy URL validation
// ---------------------------------------------------------------------------

function testValidProxyURL(): TestCase {
  const name = "valid proxy URL is accepted";
  const url = `${PROXY_BASE_URL}giphy/media/abc123/giphy.gif`;
  const ok = validateProxyURL(url);
  return ok ? pass(name) : fail(name, `rejected: ${url}`);
}

function testHttpProxyURLRejected(): TestCase {
  const name = "http:// variant is rejected (non-HTTPS)";
  const url = "http://cdn.kaether.fr/gifproxy/v1/giphy/media/abc123/giphy.gif";
  const ok = !validateProxyURL(url);
  return ok ? pass(name) : fail(name, `accepted http:// URL: ${url}`);
}

function testWrongHostRejected(): TestCase {
  const name = "wrong host is rejected";
  const url = "https://evil.com/gifproxy/v1/giphy/media/abc123/giphy.gif";
  const ok = !validateProxyURL(url);
  return ok ? pass(name) : fail(name, `accepted wrong-host URL: ${url}`);
}

function testExternalRedirectInQueryPathRejected(): TestCase {
  const name = "query path with :// is rejected (external-redirect attempt)";
  // An attacker might inject ?redirect=http://evil.com/x after the proxy base.
  const url = `${PROXY_BASE_URL}giphy/search?url=https://evil.com/x.gif`;
  const ok = !validateProxyURL(url);
  return ok ? pass(name) : fail(name, `accepted URL with :// in query path: ${url}`);
}

function testRedirectChainAllProxy(): TestCase {
  const name = "redirect chain all on proxy → accepted";
  const chain = [
    `${PROXY_BASE_URL}giphy/media/abc/giphy.gif`,
    `${PROXY_BASE_URL}giphy/media/abc/200.gif`,
  ];
  const ok = validateResponseURL(chain);
  return ok ? pass(name) : fail(name, `chain rejected: ${JSON.stringify(chain)}`);
}

function testRedirectChainWithExternalStepRejected(): TestCase {
  const name = "redirect chain with external step → rejected";
  const chain = [
    `${PROXY_BASE_URL}giphy/media/abc/giphy.gif`,
    "https://media.giphy.com/media/abc/giphy.gif", // escaped the proxy
    `${PROXY_BASE_URL}giphy/media/abc/200.gif`,
  ];
  const ok = !validateResponseURL(chain);
  return ok ? pass(name) : fail(name, "chain with external step was accepted");
}

// ---------------------------------------------------------------------------
// Group B: GifSearchResultItem shape + rewriteProviderURL
// ---------------------------------------------------------------------------

function testGifSearchResultItemHasNoTrackingFields(): TestCase {
  const name = "GifSearchResultItem type has no trackingPixel / analyticsId field";
  // This is a type-level test — we construct an item and verify that no
  // tracking-related properties are present on the object shape.
  // TypeScript enforces this at compile time; at runtime we assert the fields
  // are not present on a representative object.
  const item: GifSearchResultItem = {
    id: "abc123",
    previewUrl: `${PROXY_BASE_URL}giphy/media/abc123/200w.gif`,
    fullUrl: `${PROXY_BASE_URL}giphy/media/abc123/giphy.gif`,
    width: 480,
    height: 270,
  };

  // Runtime shape check: none of the forbidden fields should exist.
  const itemAsRecord = item as Record<string, unknown>;
  const forbidden = [
    "trackingPixel",
    "trackingUrls",
    "analyticsId",
    "analyticsTag",
    "adMetadata",
    "sponsored",
    "tags",
  ];
  const present = forbidden.filter((f) => f in itemAsRecord);
  const ok = present.length === 0;
  return ok
    ? pass(name)
    : fail(name, `forbidden fields present: ${present.join(", ")}`);
}

function testRewriteGiphyURL(): TestCase {
  const name = "rewriteProviderURL rewrites giphy.com URL through proxy";
  const raw = "https://media.giphy.com/media/abc123/giphy.gif";
  let rewritten: string;
  try {
    rewritten = rewriteProviderURL(raw);
  } catch (e) {
    return fail(name, `threw: ${String(e)}`);
  }
  const ok =
    rewritten.startsWith(PROXY_BASE_URL) &&
    validateProxyURL(rewritten) &&
    rewritten.includes("giphy") &&
    rewritten.includes("media/abc123/giphy.gif");
  return ok
    ? pass(name)
    : fail(name, `unexpected rewrite: ${rewritten}`);
}

function testRewriteTenorURL(): TestCase {
  const name = "rewriteProviderURL rewrites tenor.com URL through proxy";
  const raw = "https://media.tenor.com/images/abc/tenor.gif";
  let rewritten: string;
  try {
    rewritten = rewriteProviderURL(raw);
  } catch (e) {
    return fail(name, `threw: ${String(e)}`);
  }
  const ok =
    rewritten.startsWith(PROXY_BASE_URL) &&
    validateProxyURL(rewritten) &&
    rewritten.includes("tenor") &&
    rewritten.includes("images/abc/tenor.gif");
  return ok
    ? pass(name)
    : fail(name, `unexpected rewrite: ${rewritten}`);
}

function testRewriteRejectsHttpInput(): TestCase {
  const name = "rewriteProviderURL rejects http:// input";
  const raw = "http://media.giphy.com/media/abc123/giphy.gif";
  try {
    const result = rewriteProviderURL(raw);
    return fail(name, `expected throw but got: ${result}`);
  } catch (e) {
    if (e instanceof TypeError) {
      return pass(name);
    }
    return fail(name, `expected TypeError but got: ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Group C: Sticker manifest — valid round-trip + signature
// ---------------------------------------------------------------------------

async function testStickerPackRoundTrip(): Promise<TestCase> {
  const name = "sticker pack encode → decode preserves all stickers";
  try {
    const author = await generateAuthorKeyPair();
    const stickers: StickerEntry[] = [
      freshSticker("s1"),
      freshSticker("s2"),
      freshSticker("s3"),
    ];
    const wire = await encodeStickerPack(
      "Test Pack",
      stickers,
      author.privateKey,
      author.publicKeyBytes,
    );
    const decoded = await decodeStickerPack(wire);

    const nameOk = decoded.name === "Test Pack";
    const countOk = decoded.stickers.length === 3;
    const idsOk = decoded.stickers.every(
      (s, i) => s.id === stickers[i].id,
    );
    const urlsOk = decoded.stickers.every(
      (s, i) => s.url === stickers[i].url,
    );
    const altOk = decoded.stickers.every(
      (s, i) => s.altText === stickers[i].altText,
    );
    const pubkeyOk =
      decoded.authorPublicKey.length === 32 &&
      decoded.authorPublicKey.every(
        (b, i) => b === author.publicKeyBytes[i],
      );

    const ok = nameOk && countOk && idsOk && urlsOk && altOk && pubkeyOk;
    return ok
      ? pass(name)
      : fail(
          name,
          `name=${nameOk} count=${countOk} ids=${idsOk} urls=${urlsOk} alt=${altOk} pubkey=${pubkeyOk}`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testStickerPackSignatureVerifies(): Promise<TestCase> {
  const name = "Ed25519 signature verifies on decoded pack";
  try {
    const author = await generateAuthorKeyPair();
    const wire = await encodeStickerPack(
      "Signed Pack",
      [freshSticker("s1")],
      author.privateKey,
      author.publicKeyBytes,
    );
    // decodeStickerPack throws StickerManifestError { tag: "invalidSignature" }
    // on failure — if it does not throw, the signature is valid.
    await decodeStickerPack(wire);
    return pass(name);
  } catch (e) {
    return fail(name, `decode threw: ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Group D: Rejection cases
// ---------------------------------------------------------------------------

async function testEmptyPackRejected(): Promise<TestCase> {
  const name = "0 stickers → emptyPack error";
  try {
    const author = await generateAuthorKeyPair();
    const wire = await encodeStickerPack(
      "Empty Pack",
      [], // 0 stickers — should throw
      author.privateKey,
      author.publicKeyBytes,
    );
    return fail(name, `expected throw but encoded: ${wire.slice(0, 60)}…`);
  } catch (e) {
    if (e instanceof StickerManifestError && e.kind.tag === "emptyPack") {
      return pass(name);
    }
    return fail(name, `expected emptyPack error, got: ${String(e)}`);
  }
}

async function testTooManyStickersRejected(): Promise<TestCase> {
  const name = `${STICKER_PACK_MAX_STICKERS + 1} stickers → tooManyStickers error`;
  try {
    const author = await generateAuthorKeyPair();
    const stickers = Array.from(
      { length: STICKER_PACK_MAX_STICKERS + 1 },
      (_, i) => freshSticker(`s${i}`),
    );
    const wire = await encodeStickerPack(
      "Big Pack",
      stickers,
      author.privateKey,
      author.publicKeyBytes,
    );
    return fail(name, `expected throw but encoded: ${wire.slice(0, 60)}…`);
  } catch (e) {
    if (
      e instanceof StickerManifestError &&
      e.kind.tag === "tooManyStickers"
    ) {
      return pass(name);
    }
    return fail(name, `expected tooManyStickers, got: ${String(e)}`);
  }
}

async function testDuplicateStickerIdRejected(): Promise<TestCase> {
  const name = "duplicate sticker ID → duplicateStickerIds error";
  try {
    const author = await generateAuthorKeyPair();
    const stickers: StickerEntry[] = [
      freshSticker("dup"),
      freshSticker("dup"), // duplicate
    ];
    const wire = await encodeStickerPack(
      "Dup Pack",
      stickers,
      author.privateKey,
      author.publicKeyBytes,
    );
    return fail(name, `expected throw but encoded: ${wire.slice(0, 60)}…`);
  } catch (e) {
    if (
      e instanceof StickerManifestError &&
      e.kind.tag === "duplicateStickerIds"
    ) {
      return pass(name);
    }
    return fail(name, `expected duplicateStickerIds, got: ${String(e)}`);
  }
}

async function testStickerBadURLRejected(): Promise<TestCase> {
  const name = "sticker URL fails validateProxyURL → invalidStickerURL error";
  try {
    const author = await generateAuthorKeyPair();
    const stickers: StickerEntry[] = [
      {
        id: "bad-url-sticker",
        url: "https://evil.com/sticker.gif", // not on proxy
        altText: "bad",
      },
    ];
    const wire = await encodeStickerPack(
      "Bad URL Pack",
      stickers,
      author.privateKey,
      author.publicKeyBytes,
    );
    return fail(name, `expected throw but encoded: ${wire.slice(0, 60)}…`);
  } catch (e) {
    if (
      e instanceof StickerManifestError &&
      e.kind.tag === "invalidStickerURL"
    ) {
      return pass(name);
    }
    return fail(name, `expected invalidStickerURL, got: ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "GIF / sticker (proxy + manifest)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Proxy URL validation
        Promise.resolve(testValidProxyURL()),
        Promise.resolve(testHttpProxyURLRejected()),
        Promise.resolve(testWrongHostRejected()),
        Promise.resolve(testExternalRedirectInQueryPathRejected()),
        Promise.resolve(testRedirectChainAllProxy()),
        Promise.resolve(testRedirectChainWithExternalStepRejected()),
        // B. GifSearchResultItem shape + rewriteProviderURL
        Promise.resolve(testGifSearchResultItemHasNoTrackingFields()),
        Promise.resolve(testRewriteGiphyURL()),
        Promise.resolve(testRewriteTenorURL()),
        Promise.resolve(testRewriteRejectsHttpInput()),
        // C. Sticker manifest — valid round-trip + signature
        testStickerPackRoundTrip(),
        testStickerPackSignatureVerifies(),
        // D. Rejection cases
        testEmptyPackRejected(),
        testTooManyStickersRejected(),
        testDuplicateStickerIdRejected(),
        testStickerBadURLRejected(),
      ]),
    ),
});
